import {
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
  FaSearchMinus,
  FaExpand,
  FaCompress,
  FaFilePdf,
  FaSave,
} from 'react-icons/fa';
import { useVideoMaterialViewer } from './useVideoMaterialViewer';
import { VideoMaterialViewerProps } from './types';
import styles from './VideoMaterialViewer.module.css';

/**
 * 動画詳細画面の「資料」欄。
 * 複数の資料があればファイル名タブで切り替え、pdf.js でページ送り・拡大縮小・
 * 全画面表示を行う。「元のファイルを保存」は院内ネットワークからのみ活性。
 */
const VideoMaterialViewer = (props: VideoMaterialViewerProps) => {
  const {
    materials,
    selectedMaterial,
    selectMaterial,
    isLoadingList,
    isLoadingPdf,
    loadError,
    canvasRef,
    containerRef,
    canvasWrapperRef,
    currentPage,
    numPages,
    goToPrevPage,
    goToNextPage,
    scale,
    zoomIn,
    zoomOut,
    isFullscreen,
    toggleFullscreen,
    isExternalNetwork,
    isSavingOriginal,
    handleSaveOriginal,
  } = useVideoMaterialViewer(props.videoId);

  if (isLoadingList || materials.length === 0) {
    return null;
  }

  return (
    <div className={styles['video-material-viewer']} data-testid="video-material-viewer">
      <h2 className={styles['video-material-viewer__heading']}>資料</h2>
      {materials.length > 1 && (
        <div className={styles['video-material-viewer__tabs']} data-testid="video-material-viewer__tabs">
          {materials.map(material => (
            <button
              key={material.id}
              type="button"
              className={`${styles['video-material-viewer__tab']} ${selectedMaterial?.id === material.id ? styles['video-material-viewer__tab--active'] : ''}`}
              onClick={() => selectMaterial(material.id)}
              title={material.original_filename}
            >
              <FaFilePdf />
              <span>{material.original_filename}</span>
            </button>
          ))}
        </div>
      )}

      <div
        className={`${styles['video-material-viewer__stage']} ${isFullscreen ? styles['video-material-viewer__stage--fullscreen'] : ''}`}
        ref={containerRef}
        data-testid="video-material-viewer__stage"
      >
        <div className={styles['video-material-viewer__toolbar']}>
          <button type="button" onClick={goToPrevPage} disabled={currentPage <= 1} aria-label="前のページ" data-testid="video-material-viewer__prev-page">
            <FaChevronLeft />
          </button>
          <span className={styles['video-material-viewer__page-counter']} data-testid="video-material-viewer__page-counter">
            {numPages > 0 ? `${currentPage} / ${numPages}` : '-'}
          </span>
          <button type="button" onClick={goToNextPage} disabled={currentPage >= numPages} aria-label="次のページ" data-testid="video-material-viewer__next-page">
            <FaChevronRight />
          </button>

          <button type="button" onClick={zoomOut} aria-label="縮小" data-testid="video-material-viewer__zoom-out">
            <FaSearchMinus />
          </button>
          <span className={styles['video-material-viewer__zoom-level']}>{Math.round(scale * 100)}%</span>
          <button type="button" onClick={zoomIn} aria-label="拡大" data-testid="video-material-viewer__zoom-in">
            <FaSearch />
          </button>

          <button type="button" onClick={toggleFullscreen} aria-label={isFullscreen ? '全画面を終了' : '全画面表示'} data-testid="video-material-viewer__fullscreen-toggle">
            {isFullscreen ? <FaCompress /> : <FaExpand />}
          </button>

          <button
            type="button"
            onClick={handleSaveOriginal}
            disabled={isExternalNetwork || isSavingOriginal || !selectedMaterial}
            title={isExternalNetwork ? '資料の原本保存は院内ネットワークからのみ可能です' : '元のファイルを保存'}
            aria-disabled={isExternalNetwork || isSavingOriginal || !selectedMaterial}
            className={styles['video-material-viewer__save-button']}
            data-testid="video-material-viewer__save-original"
          >
            <FaSave />
            <span>元のファイルを保存</span>
          </button>
        </div>

        <div className={styles['video-material-viewer__canvas-wrapper']} ref={canvasWrapperRef}>
          {isLoadingPdf && (
            <p className={styles['video-material-viewer__status']} data-testid="video-material-viewer__loading">資料を読み込み中...</p>
          )}
          {loadError && (
            <p className={styles['video-material-viewer__status']} data-testid="video-material-viewer__error">{loadError}</p>
          )}
          <canvas ref={canvasRef} className={styles['video-material-viewer__canvas']} data-testid="video-material-viewer__canvas" />
        </div>
      </div>
    </div>
  );
};

export default VideoMaterialViewer;
